import { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import { STREET_TILES } from '../lib/mapTiles';
import { useToast } from '../components/ui/Toast';
import {
  MapContainer, TileLayer, Marker, Popup, Tooltip, Circle, Polyline, Polygon, CircleMarker, GeoJSON, useMapEvents,
} from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet/dist/leaflet.css';
import type { LatLngBounds } from 'leaflet';
import type {
  MapProperty, MapPermit, MapTenant, RadiusCircle, MapFilters, MapLayers, SavedSearchCriteria,
} from '../components/map/types';
import { EMPTY_FILTERS } from '../components/map/types';
import { propertyBubbleIcon, permitDiamondIcon, ringCenterIcon, distressLevel, clusterIcon, tenantIcon, tenantExpiryColor } from '../components/map/markerIcons';
import { pointInPolygon } from '../components/map/geo';
import RadiusCircleLayer from '../components/map/RadiusCircleLayer';
import { buildMedianPsf, dealFlagFor, type DealFlag } from '../components/map/dealFlags';
import MapResultsList from '../components/map/MapResultsList';
import MapController from '../components/map/MapController';
import MapFilterBar from '../components/map/MapFilterBar';
import MapFiltersPanel, { type FilterOptions } from '../components/map/MapFiltersPanel';
import SavedSearchBar from '../components/map/SavedSearchBar';
import DemographicsPanel from '../components/DemographicsPanel';
import SpendingSegmentsPanel from '../components/SpendingSegmentsPanel';
import PropertyComparison from '../components/PropertyComparison';
import { toComparisonProperty, exportCompsCsv } from '../components/map/compUtils';
import { generateCompReportPDF } from '../utils/pdfBuilder';
import SaveCompsModal from '../components/map/SaveCompsModal';

// ── Helpers ──

function formatCurrency(val?: number | null): string {
  if (!val) return '—';
  if (val >= 1_000_000) return '$' + (val / 1_000_000).toFixed(2) + 'M';
  if (val >= 1_000) return '$' + (val / 1_000).toFixed(0) + 'K';
  return '$' + val.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

const DEFAULT_CENTER: [number, number] = [34.05, -117.4];
const DEFAULT_ZOOM = 10;
const RING_MILES = [1, 3, 5];

// Routes map clicks to the active tool (radius add / draw vertex + trade area).
function MapClickHandler({
  radiusMode, drawMode, tradeAreaMode, onRadius, onVertex, onFinish, onTradeArea,
}: {
  radiusMode: boolean; drawMode: boolean; tradeAreaMode: boolean;
  onRadius: (lat: number, lng: number) => void;
  onVertex: (lat: number, lng: number) => void;
  onFinish: () => void;
  onTradeArea: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click(e) {
      if (drawMode) onVertex(e.latlng.lat, e.latlng.lng);
      else if (radiusMode) onRadius(e.latlng.lat, e.latlng.lng);
      else if (tradeAreaMode) onTradeArea(e.latlng.lat, e.latlng.lng);
    },
    dblclick() { if (drawMode) onFinish(); },
  });
  return null;
}

// Reports the visible map bounds so the list can sync to the current view.
function BoundsWatcher({ onBounds }: { onBounds: (b: LatLngBounds) => void }) {
  const map = useMapEvents({
    moveend: () => onBounds(map.getBounds()),
    zoomend: () => onBounds(map.getBounds()),
  });
  useEffect(() => { onBounds(map.getBounds()); }, [map, onBounds]);
  return null;
}

// Shared popup content (used by the selected-property overlay marker).
function PropertyPopupBody({ p, selected, onDemographics, onToggleSelect }: {
  p: MapProperty; selected: boolean;
  onDemographics: (p: MapProperty) => void;
  onToggleSelect: (p: MapProperty) => void;
}) {
  const psf = p.price && p.building_size ? Math.round(p.price / p.building_size) : null;
  const distress = distressLevel(p);
  return (
    <div className="text-xs space-y-2 w-[236px]">
      {p.primary_photo_url && (
        <div className="relative">
          <img src={p.primary_photo_url} alt="" className="w-full h-28 object-cover rounded-lg ring-1 ring-gray-200" />
          {p.primary_photo_url.includes('/street-view/') && (
            <span className="absolute bottom-1.5 left-1.5 bg-black/65 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-md">Street View</span>
          )}
        </div>
      )}
      <div>
        <p className="font-semibold text-sm text-gray-900 leading-snug">{p.address || 'No address'}</p>
        <p className="text-gray-500 mt-0.5">{[p.city, p.state, p.zip].filter(Boolean).join(', ')}</p>
      </div>
      {(distress || p.price) && (
        <div className="flex items-center justify-between gap-2">
          {p.price ? (
            <p className="text-base font-bold text-gray-900 tracking-tight">{formatCurrency(p.price)}{psf ? <span className="text-xs font-normal text-gray-400"> ${psf}/SF</span> : null}</p>
          ) : <span />}
          {distress && (
            <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded ring-1 ${distress === 'past_due' ? 'bg-primary-100 text-primary-500 ring-primary-200' : 'bg-warning-wash text-warning ring-warning/30'}`}>
              {distress === 'past_due' ? '🏦 Loan past due' : '🏦 Loan maturing'}
            </span>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 border-t border-gray-100 pt-2 text-gray-600">
        {p.building_size ? <div><span className="text-gray-400">Size</span><br />{p.building_size.toLocaleString()} SF</div> : null}
        {p.property_type ? <div><span className="text-gray-400">Type</span><br />{p.property_type.replace(/_/g, ' ')}</div> : null}
        {p.year_built ? <div><span className="text-gray-400">Built</span><br />{p.year_built}</div> : null}
        {p.percent_leased != null ? <div><span className="text-gray-400">Leased</span><br />{p.percent_leased}%</div> : null}
        {p.owner_name ? <div className="col-span-2"><span className="text-gray-400">Owner</span><br />{p.owner_name}</div> : null}
      </div>
      <button
        onClick={() => onToggleSelect(p)}
        className={`w-full mt-0.5 px-2 py-1.5 rounded-lg font-medium border transition-colors ${selected ? 'bg-gray-900 text-white border-gray-900 hover:bg-gray-800' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100'}`}
      >{selected ? '✓ Selected for comps' : '+ Select for comps'}</button>
      <div className="flex items-center gap-3 pt-0.5">
        <a href={`/data-hub/${p.id}`} className="text-primary-500 hover:underline font-semibold">View in Data Hub</a>
        <button onClick={() => onDemographics(p)} className="text-primary-500 hover:underline font-semibold">Demographics</button>
      </div>
    </div>
  );
}

// Clustered pins are click-to-select only; the popup is shown by the dedicated
// SelectedPropertyMarker overlay below (so a card click reliably reveals the
// property even when it's collapsed inside a cluster). Memoized so hovering one
// card/pin doesn't re-render every marker.
const PropertyMarker = memo(function PropertyMarker({
  p, active, selected, onSelect, onHover,
}: {
  p: MapProperty; active: boolean; selected: boolean;
  onSelect: (p: MapProperty) => void;
  onHover: (id: string | null) => void;
}) {
  return (
    <Marker
      position={[p.latitude!, p.longitude!]}
      icon={propertyBubbleIcon(p, { active, selected, distress: distressLevel(p) ?? undefined })}
      eventHandlers={{
        click: () => onSelect(p),
        mouseover: () => onHover(p.id),
        mouseout: () => onHover(null),
      }}
    />
  );
}, (a, b) => a.active === b.active && a.selected === b.selected && a.p === b.p);

// The selected property gets a dedicated marker OUTSIDE the cluster group, so it
// is always visible (never collapsed into a cluster) with its popup auto-open.
// This is what makes "click a card -> see the property on the map" work.
function SelectedPropertyMarker({ p, selected, onHover, onDemographics, onToggleSelect }: {
  p: MapProperty; selected: boolean;
  onHover: (id: string | null) => void;
  onDemographics: (p: MapProperty) => void;
  onToggleSelect: (p: MapProperty) => void;
}) {
  const ref = useRef<any>(null);
  useEffect(() => {
    // Open after the flyTo starts; autoPan is off so it won't fight the animation.
    const t = setTimeout(() => ref.current?.openPopup(), 250);
    return () => clearTimeout(t);
  }, [p.id]);
  return (
    <Marker
      ref={ref}
      position={[p.latitude!, p.longitude!]}
      icon={propertyBubbleIcon(p, { active: true, selected, distress: distressLevel(p) ?? undefined })}
      zIndexOffset={1000}
      eventHandlers={{ mouseover: () => onHover(p.id), mouseout: () => onHover(null) }}
    >
      <Popup maxWidth={280} minWidth={200} autoPan={false}>
        <PropertyPopupBody p={p} selected={selected} onDemographics={onDemographics} onToggleSelect={onToggleSelect} />
      </Popup>
    </Marker>
  );
}

export default function ProspectingMap() {
  const { toast } = useToast();
  const [properties, setProperties] = useState<MapProperty[]>([]);
  const [permits, setPermits] = useState<MapPermit[]>([]);
  const [tenants, setTenants] = useState<MapTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<MapFilters>({ ...EMPTY_FILTERS });
  const [layers, setLayers] = useState<MapLayers>({ properties: true, permits: true, tenants: false });
  const [mobileView, setMobileView] = useState<'map' | 'list'>('map');

  // List <-> map sync
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Comp tray: checkbox selection that persists across pans/filters. Stores the
  // full property object so the tray survives even when a pick leaves the view.
  const [selected, setSelected] = useState<Map<string, MapProperty>>(new Map());

  // Demographic rings. `isProperty` marks the center as a subject/target property
  // (address label) vs a blank map tap; used by the trade-area report + center marker.
  const [ringCenter, setRingCenter] = useState<{ lat: number; lng: number; label: string; isProperty?: boolean; propertyId?: string } | null>(null);
  // Active trade-area radii (kept in sync with DemographicsPanel so the map rings match).
  const [ringRadii, setRingRadii] = useState<number[]>(RING_MILES);

  // Radius circles (multiple, combined)
  const [radiusMode, setRadiusMode] = useState(false);
  const [radiusMiles, setRadiusMiles] = useState(5);
  const [circles, setCircles] = useState<RadiusCircle[]>([]);
  const [circleResults, setCircleResults] = useState<Record<number, MapProperty[]>>({});
  const circleSeq = useRef(1);
  const circlesRef = useRef<RadiusCircle[]>([]);
  useEffect(() => { circlesRef.current = circles; }, [circles]);

  // Draw-polygon
  const [drawMode, setDrawMode] = useState(false);
  const [drawVertices, setDrawVertices] = useState<[number, number][]>([]);
  const [polygon, setPolygon] = useState<[number, number][] | null>(null);

  // Trade-area rings (tap any point -> concentric 1/3/5-mi rings + demographics)
  const [tradeAreaMode, setTradeAreaMode] = useState(false);
  const [driveTimeMode, setDriveTimeMode] = useState(false); // distance vs drive-time
  const [driveContours, setDriveContours] = useState<any[] | null>(null);
  const [driveBusy, setDriveBusy] = useState(false);

  // Listings panel: text search (whole DB) + map-bounds sync
  const [searchText, setSearchText] = useState('');
  const [bounds, setBounds] = useState<LatLngBounds | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [saveCompsOpen, setSaveCompsOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // AI comp finder
  const [aiQuery, setAiQuery] = useState('');
  const [aiComps, setAiComps] = useState<MapProperty[] | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNonce, setAiNonce] = useState(0);

  // Deal radar: show only below-market opportunities
  const [dealsOnly, setDealsOnly] = useState(false);
  // Distress radar: show only properties with a loan (maturity/distress) from Capitalize
  const [distressOnly, setDistressOnly] = useState(false);
  const [ghlBusy, setGhlBusy] = useState(false);

  // ── Fetch properties ONCE (all of them), then filter client-side so filters
  // are instant and the filter option lists stay complete (picking a Type must
  // not collapse the Type list). 2,590 rows is small enough to filter locally. ──
  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getMapProperties();
      setProperties(res.properties || []);
    } catch (e) {
      console.error('Failed to load map properties:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);

  useEffect(() => {
    api.getMapPermits().then((res) => setPermits(res.permits || [])).catch(() => {});
    api.getMapTenantProspects().then((res) => setTenants(res.tenants || [])).catch(() => {});
  }, []);

  // ── Derived data ── (option lists come from the FULL set, so they never shrink)
  const uniq = (vals: (string | undefined)[]) => [...new Set(vals.filter(Boolean))].sort() as string[];
  const permitPins = useMemo(() => permits.filter((p) => p.latitude && p.longitude), [permits]);
  const tenantPins = useMemo(() => tenants.filter((t) => t.latitude && t.longitude), [tenants]);

  // Earliest lease expiration (epoch ms) per linked property, for the
  // "Expiring tenant" filter. Tenants without a master_property_id link or a
  // parseable lease_expiration are skipped.
  const tenantExpiryByProperty = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of tenants) {
      if (!t.master_property_id || !t.lease_expiration) continue;
      const ts = new Date(t.lease_expiration).getTime();
      if (Number.isNaN(ts)) continue;
      const prev = m.get(t.master_property_id);
      if (prev == null || ts < prev) m.set(t.master_property_id, ts);
    }
    return m;
  }, [tenants]);

  // Call in GHL: push the tenant to GHL (contact upsert) then open their GHL page in
  // a new tab, where GHL's dialer takes over (no in-app Twilio dialer needed).
  const handleCallInGhl = useCallback(async (t: MapTenant) => {
    const w = window.open('', '_blank'); // open synchronously to dodge popup blockers
    try {
      const res: any = await api.pushTenantToGhl(t.id);
      if (res?.ghlUrl && w) w.location.href = res.ghlUrl;
      else { if (w) w.close(); toast.error('Could not open GHL for this tenant (is GHL configured?)'); }
    } catch {
      if (w) w.close();
      toast.error('Failed to push this tenant to GHL');
    }
  }, [toast]);
  const cities = useMemo(() => uniq(properties.map((p) => p.city)), [properties]);
  const types = useMemo(() => uniq(properties.map((p) => p.property_type)), [properties]);
  const subtypes = useMemo(() => uniq(properties.map((p) => p.property_subtype)), [properties]);
  const classes = useMemo(() => uniq(properties.map((p) => p.building_class)), [properties]);
  const submarkets = useMemo(() => uniq(properties.map((p) => p.submarket)), [properties]);
  const counties = useMemo(() => uniq(properties.map((p) => p.county)), [properties]);
  const tenancies = useMemo(() => uniq(properties.map((p) => p.tenancy)), [properties]);
  const pipelineStatuses = useMemo(() => uniq(properties.map((p) => p.pipeline_status)), [properties]);
  const filterOptions: FilterOptions = useMemo(
    () => ({ types, subtypes, classes, submarkets, counties, cities, tenancies, pipelineStatuses }),
    [types, subtypes, classes, submarkets, counties, cities, tenancies, pipelineStatuses],
  );

  // Market baseline for the deal radar: median $/SF by submarket+type over the
  // FULL loaded set (independent of the active filters).
  const medianPsf = useMemo(() => buildMedianPsf(properties), [properties]);

  // All filters applied client-side (instant, no refetch).
  const filtered = useMemo(() => {
    const f = filter;
    const nMinP = f.min_price ? +f.min_price : null, nMaxP = f.max_price ? +f.max_price : null;
    const nMinSf = f.min_building_size ? +f.min_building_size : null, nMaxSf = f.max_building_size ? +f.max_building_size : null;
    const nMinAc = f.min_lot_acres ? +f.min_lot_acres : null, nMaxAc = f.max_lot_acres ? +f.max_lot_acres : null;
    const nMinYr = f.year_built_min ? +f.year_built_min : null, nMaxYr = f.year_built_max ? +f.year_built_max : null;
    const nMinPsf = f.min_psf ? +f.min_psf : null, nMaxPsf = f.max_psf ? +f.max_psf : null;
    const nMinCap = f.min_cap ? +f.min_cap : null, nMaxCap = f.max_cap ? +f.max_cap : null;
    const nMinDom = f.min_dom ? +f.min_dom : null, nMaxDom = f.max_dom ? +f.max_dom : null;
    const city = f.city.toLowerCase(), zoning = f.zoning.trim().toLowerCase();
    // Expiring-tenant window: earliest tenant expiration must land between today
    // (past-expired excluded) and N months out.
    let expStart: number | null = null, expEnd: number | null = null;
    // ⛔ Any positive month count, not a hardcoded pair. It read `'12' || '24'`, so when the
    //   options moved to Robert's 6/12/18 bands the 6- and 18-month choices would have silently
    //   filtered NOTHING while still looking selected.
    if (/^\d+$/.test(f.expiring_tenant) && Number(f.expiring_tenant) > 0) {
      const now = new Date();
      expStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime(); // start of today
      const end = new Date(now);
      end.setMonth(end.getMonth() + Number(f.expiring_tenant));
      expEnd = end.getTime();
    }
    return properties.filter((p) => {
      if (!p.latitude || !p.longitude) return false;
      if (f.type && p.property_type !== f.type) return false;
      if (f.subtype && p.property_subtype !== f.subtype) return false;
      if (f.building_class && p.building_class !== f.building_class) return false;
      if (f.submarket && p.submarket !== f.submarket) return false;
      if (f.county && p.county !== f.county) return false;
      if (f.tenancy && p.tenancy !== f.tenancy) return false;
      if (city && (p.city || '').toLowerCase() !== city) return false;
      if (zoning && !(p.zoning || '').toLowerCase().includes(zoning)) return false;
      if (f.pipeline_status && p.pipeline_status !== f.pipeline_status) return false;
      if (f.opportunity_zone && p.opportunity_zone !== true) return false;
      if (f.has_photos && !(p.photo_count && p.photo_count > 0)) return false;
      if (expStart != null && expEnd != null) {
        const exp = tenantExpiryByProperty.get(p.id);
        if (exp == null || exp < expStart || exp > expEnd) return false;
      }
      if (nMinP != null && !(p.price != null && p.price >= nMinP)) return false;
      if (nMaxP != null && !(p.price != null && p.price <= nMaxP)) return false;
      if (nMinSf != null && !(p.building_size != null && p.building_size >= nMinSf)) return false;
      if (nMaxSf != null && !(p.building_size != null && p.building_size <= nMaxSf)) return false;
      if (nMinAc != null && !(p.lot_size_acres != null && p.lot_size_acres >= nMinAc)) return false;
      if (nMaxAc != null && !(p.lot_size_acres != null && p.lot_size_acres <= nMaxAc)) return false;
      if (nMinYr != null && !(p.year_built != null && p.year_built >= nMinYr)) return false;
      if (nMaxYr != null && !(p.year_built != null && p.year_built <= nMaxYr)) return false;
      if (nMinCap != null && !(p.cap_rate != null && p.cap_rate >= nMinCap)) return false;
      if (nMaxCap != null && !(p.cap_rate != null && p.cap_rate <= nMaxCap)) return false;
      if (nMinDom != null && !(p.days_on_market != null && p.days_on_market >= nMinDom)) return false;
      if (nMaxDom != null && !(p.days_on_market != null && p.days_on_market <= nMaxDom)) return false;
      if (nMinPsf != null || nMaxPsf != null) {
        const psf = p.price != null && p.building_size ? p.price / p.building_size : null;
        if (nMinPsf != null && !(psf != null && psf >= nMinPsf)) return false;
        if (nMaxPsf != null && !(psf != null && psf <= nMaxPsf)) return false;
      }
      if (dealsOnly && !dealFlagFor(p, medianPsf)?.belowMarketPct) return false;
      if (distressOnly && !p.loan) return false;
      return true;
    });
  }, [properties, filter, dealsOnly, distressOnly, medianPsf, tenantExpiryByProperty]);

  // Push the distressed owners currently in view to GHL (hottest 100 by maturity).
  const handlePushDistressToGhl = useCallback(async () => {
    const inView = filtered.filter((p) => p.loan);
    const sorted = [...inView].sort((a, b) => (a.loan?.maturity_date || '9999').localeCompare(b.loan?.maturity_date || '9999'));
    const ids = sorted.slice(0, 100).map((p) => p.id);
    if (ids.length === 0) return;
    if (!window.confirm(`Add ${ids.length} distressed-property owner${ids.length === 1 ? '' : 's'} (hottest by loan maturity) to GoHighLevel for outreach?\n\nDo-Not-Call numbers are skipped automatically.`)) return;
    setGhlBusy(true);
    try {
      const res = await api.pushLoansToGhl(ids);
      const detailParts: string[] = [];
      if (res.skipped_no_contact) detailParts.push(`${res.skipped_no_contact} skipped (no reachable contact or all Do-Not-Call).`);
      if (inView.length > ids.length) detailParts.push(`${inView.length - ids.length} more distressed in view — narrow the map to push them.`);
      toast.success(`Added ${res.pushed} owner(s) to GHL`, detailParts.length ? detailParts.join('\n') : undefined);
    } catch (e: any) {
      toast.error('GHL push failed', e?.message || 'error');
    } finally {
      setGhlBusy(false);
    }
  }, [filtered, toast]);

  const areaUnion = useMemo(() => {
    if (circles.length === 0) return null;
    const seen = new Map<string, MapProperty>();
    circles.forEach((c) => (circleResults[c.id] || []).forEach((p) => {
      if (p.latitude && p.longitude) seen.set(p.id, p);
    }));
    return [...seen.values()];
  }, [circles, circleResults]);

  const polygonMatches = useMemo(() => {
    if (!polygon || polygon.length < 3) return null;
    return filtered.filter((p) => pointInPolygon([p.latitude!, p.longitude!], polygon));
  }, [polygon, filtered]);

  // Selection (draw/radius) narrows the comp set.
  const selection = useMemo(() => {
    const parts: MapProperty[][] = [];
    if (areaUnion) parts.push(areaUnion);
    if (polygonMatches) parts.push(polygonMatches);
    if (parts.length === 0) return null;
    const seen = new Map<string, MapProperty>();
    parts.flat().forEach((p) => seen.set(p.id, p));
    return [...seen.values()];
  }, [areaUnion, polygonMatches]);

  // Right-list contents (hybrid): text search spans the whole DB; otherwise a
  // draw/radius selection wins; otherwise the list syncs to the map view.
  const listItems = useMemo(() => {
    if (aiComps) return aiComps; // AI results take over the list/map until cleared
    const q = searchText.trim().toLowerCase();
    if (q) {
      return filtered.filter((p) =>
        (p.address || '').toLowerCase().includes(q) ||
        (p.city || '').toLowerCase().includes(q) ||
        (p.submarket || '').toLowerCase().includes(q) ||
        (p.zip || '').toLowerCase().includes(q) ||
        (p.owner_name || '').toLowerCase().includes(q));
    }
    if (selection) return selection;
    if (bounds) return filtered.filter((p) => bounds.contains([p.latitude!, p.longitude!]));
    return filtered;
  }, [aiComps, searchText, filtered, selection, bounds]);

  // Pan/zoom the map to fit search matches (CoStar "Address or Location" feel).
  // Keyed on the search text so it fits once per search, not on every render.
  // Fit the map to AI results or a text search (both narrow the visible set).
  const searchFit = useMemo(() => {
    const set = aiComps ?? (searchText.trim() ? listItems : null);
    if (!set || set.length === 0) return null;
    const lats = set.map((p) => p.latitude).filter((v): v is number => v != null);
    const lngs = set.map((p) => p.longitude).filter((v): v is number => v != null);
    if (!lats.length) return null;
    return [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]] as [[number, number], [number, number]];
  }, [aiComps, searchText, listItems]);

  // Pins on the map. AI results or a text search narrow the map to that set so
  // it isn't showing every pin while the list shows a subset. Otherwise show the
  // whole filtered market.
  const mapItems = useMemo(
    () => (aiComps ? aiComps : searchText.trim() ? listItems : filtered),
    [aiComps, searchText, listItems, filtered],
  );

  // Deal flags (below-market / stale) for the shown list, for card badges.
  const dealFlags = useMemo(() => {
    const m = new Map<string, DealFlag>();
    for (const p of listItems) { const f = dealFlagFor(p, medianPsf); if (f) m.set(p.id, f); }
    return m;
  }, [listItems, medianPsf]);

  const activeId = hoveredId || selectedId;

  // The property the overlay marker renders (from a card click, pin click, or tray).
  const selectedProperty = useMemo(() => {
    if (!selectedId) return null;
    return listItems.find((p) => p.id === selectedId)
      || filtered.find((p) => p.id === selectedId)
      || selected.get(selectedId)
      || null;
  }, [selectedId, listItems, filtered, selected]);

  // ── Comp-tray selection ──
  const selectedIds = useMemo(() => new Set(selected.keys()), [selected]);
  const selectedList = useMemo(() => [...selected.values()], [selected]);
  const hasSelection = selected.size > 0;
  // Comp actions operate on the checkbox selection when any boxes are checked,
  // otherwise fall back to the whole visible list (backward-compatible).
  const actionItems = hasSelection ? selectedList : listItems;

  const toggleSelect = useCallback((p: MapProperty) => {
    setSelected((m) => {
      const n = new Map(m);
      if (n.has(p.id)) n.delete(p.id); else n.set(p.id, p);
      return n;
    });
  }, []);
  const clearSelected = useCallback(() => setSelected(new Map()), []);
  const selectAllVisible = useCallback(() => {
    setSelected((m) => {
      const n = new Map(m);
      listItems.forEach((p) => n.set(p.id, p));
      return n;
    });
  }, [listItems]);
  const clearVisible = useCallback(() => {
    setSelected((m) => {
      const n = new Map(m);
      listItems.forEach((p) => n.delete(p.id));
      return n;
    });
  }, [listItems]);

  // ── AI comp finder ──
  const runAiSearch = useCallback(async () => {
    const q = aiQuery.trim();
    if (!q) return;
    setAiBusy(true);
    try {
      const res = await api.searchCompsAI({ query: q, subjectPropertyId: selectedId || undefined, limit: 50 });
      const comps: MapProperty[] = (res.comps || []).map((c: any) => ({ ...c, match_score: c.score ?? null }));
      setAiComps(comps);
      setAiNonce((n) => n + 1);
    } catch (e) {
      console.error('AI comp search failed:', e);
      setAiComps([]);
    }
    setAiBusy(false);
  }, [aiQuery, selectedId]);

  const clearAi = useCallback(() => { setAiComps(null); setAiQuery(''); }, []);

  // ── Stable callbacks (keep PropertyMarker memo effective) ──
  const handleHover = useCallback((id: string | null) => setHoveredId(id), []);
  const handleSelect = useCallback((p: MapProperty) => setSelectedId(p.id), []);
  const handleDemographics = useCallback((p: MapProperty) => {
    if (p.latitude && p.longitude) {
      setRingRadii(RING_MILES);
      setRingCenter({ lat: p.latitude, lng: p.longitude, label: p.address || 'Property', isProperty: true, propertyId: p.id });
    }
  }, []);

  const [reportBusy, setReportBusy] = useState(false);
  const handleCompReport = useCallback(async (items: MapProperty[], areaLabel: string) => {
    const ids = items.slice(0, 50).map((p) => p.id);
    if (ids.length === 0) return;
    setReportBusy(true);
    try {
      const res = await api.getCompData(ids);
      const doc = generateCompReportPDF({ comps: res.comps || [], companyName: 'Apex Real Estate Services', areaLabel });
      doc.save(`apex-comp-report-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch { /* ignore */ }
    setReportBusy(false);
  }, []);

  // ── Radius circles ──
  const refreshCircle = useCallback(async (id: number, lat: number, lng: number, miles: number) => {
    try {
      const res = await api.findNearbyProperties({ latitude: String(lat), longitude: String(lng), radius_miles: String(miles) });
      setCircleResults((r) => ({ ...r, [id]: res.properties || [] }));
    } catch {
      setCircleResults((r) => ({ ...r, [id]: [] }));
    }
  }, []);

  const addCircle = useCallback((lat: number, lng: number) => {
    const id = circleSeq.current++;
    setCircles((cs) => [...cs, { id, lat, lng, miles: radiusMiles }]);
    refreshCircle(id, lat, lng, radiusMiles);
  }, [radiusMiles, refreshCircle]);

  // Live drag updates (no fetch); the fetch happens once on release (commit).
  const resizeCircle = useCallback((id: number, miles: number) => {
    setCircles((cs) => cs.map((c) => (c.id === id ? { ...c, miles } : c)));
  }, []);
  const moveCircle = useCallback((id: number, lat: number, lng: number) => {
    setCircles((cs) => cs.map((c) => (c.id === id ? { ...c, lat, lng } : c)));
  }, []);
  const commitCircle = useCallback((id: number) => {
    const c = circlesRef.current.find((x) => x.id === id);
    if (c) refreshCircle(c.id, c.lat, c.lng, c.miles);
  }, [refreshCircle]);

  const removeCircle = (id: number) => {
    setCircles((cs) => cs.filter((c) => c.id !== id));
    setCircleResults((r) => { const n = { ...r }; delete n[id]; return n; });
  };

  // ── Draw polygon ──
  const addVertex = useCallback((lat: number, lng: number) => setDrawVertices((v) => [...v, [lat, lng]]), []);
  const finishDraw = () => {
    if (drawVertices.length >= 3) setPolygon(drawVertices);
    setDrawVertices([]);
    setDrawMode(false);
  };
  const cancelDraw = () => { setDrawVertices([]); setDrawMode(false); };

  // ── Saved searches ──
  const currentCriteria: SavedSearchCriteria = { filter, circles, polygon, layers, search: searchText };
  const applyCriteria = useCallback((c: SavedSearchCriteria) => {
    setFilter({ ...EMPTY_FILTERS, ...(c.filter ?? {}) });
    setSearchText(c.search ?? '');
    setLayers(c.layers ?? { properties: true, permits: true, tenants: false });
    setPolygon(c.polygon ?? null);
    const restored = c.circles ?? [];
    setCircles(restored);
    setCircleResults({});
    restored.forEach(async (circle) => {
      try {
        const res = await api.findNearbyProperties({ latitude: String(circle.lat), longitude: String(circle.lng), radius_miles: String(circle.miles) });
        setCircleResults((r) => ({ ...r, [circle.id]: res.properties || [] }));
      } catch { /* ignore */ }
    });
    circleSeq.current = Math.max(circleSeq.current, restored.reduce((m, c2) => Math.max(m, c2.id), 0) + 1);
  }, []);

  const toggleRadius = () => {
    setRadiusMode((v) => !v);
    setTradeAreaMode(false);
    if (drawMode) cancelDraw();
  };
  const toggleDraw = () => {
    setTradeAreaMode(false);
    if (drawMode) { cancelDraw(); return; }
    setDrawMode(true);
    setRadiusMode(false);
  };
  const toggleTradeArea = () => {
    setTradeAreaMode((v) => !v);
    setRadiusMode(false);
    if (drawMode) cancelDraw();
  };
  // Tap any point -> concentric rings + demographics drawer (reuses the ring engine).
  // In drive-time mode, fetch Mapbox isochrones + per-contour demographics instead.
  const handleTradeArea = useCallback(async (lat: number, lng: number) => {
    setRingRadii(RING_MILES);
    setRingCenter({ lat, lng, label: driveTimeMode ? 'Drive-time trade area' : 'Selected location', isProperty: false });
    if (!driveTimeMode) { setDriveContours(null); return; }
    setDriveBusy(true);
    setDriveContours(null);
    try {
      const res = await api.getDriveTimeDemographics(lat, lng, [5, 10, 15]);
      setDriveContours(res.contours || []);
    } catch (e) {
      console.error('Drive-time demographics failed:', e);
      setDriveContours([]);
    }
    setDriveBusy(false);
  }, [driveTimeMode]);

  return (
    <Layout fullBleed>
      <div className="flex flex-col h-full">
        {/* ── Top bar ── */}
        <div className="shrink-0 border-b bg-white px-3 py-2 space-y-2">
          <MapFilterBar
            filter={filter}
            onChange={setFilter}
            search={searchText}
            onSearch={setSearchText}
            onOpenFilters={() => setFiltersOpen(true)}
            layers={layers}
            onLayers={setLayers}
            count={listItems.length}
            loading={loading}
            options={filterOptions}
            right={<SavedSearchBar current={currentCriteria} onApply={applyCriteria} />}
          />
          {/* AI comp finder (desktop only; mobile keeps the top compact) */}
          <div className="hidden md:flex items-center gap-2">
            <div className="relative flex-1 max-w-2xl">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-primary-500 pointer-events-none">✨</span>
              <input
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') runAiSearch(); }}
                placeholder="Ask AI for comps — e.g. 'multi-tenant retail in Coachella Valley, 5-20k SF, cap under 8'"
                className="w-full border border-gray-300 bg-white rounded-full pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-400"
              />
            </div>
            <button onClick={runAiSearch} disabled={aiBusy || !aiQuery.trim()}
              className="px-3 py-1.5 text-sm font-medium rounded-md bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap">
              {aiBusy ? 'Finding…' : 'Find comps'}
            </button>
            {aiComps && (
              <span className="text-xs text-primary-500 inline-flex items-center gap-1.5 whitespace-nowrap">
                ✨ {aiComps.length} AI comp{aiComps.length === 1 ? '' : 's'}{selectedId ? ' · ranked' : ''}
                <button onClick={clearAi} className="underline hover:text-primary-900">Clear</button>
              </span>
            )}
          </div>
          {/* Contextual tool controls + selection chips (tool toggles live on the map's left rail) */}
          <div className="flex items-center gap-2 flex-wrap text-sm min-h-[30px]">
            {radiusMode && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-info">Radius: tap the map to drop a circle — tap again to add more</span>
                <input type="range" min={1} max={25} value={radiusMiles} onChange={(e) => setRadiusMiles(parseInt(e.target.value, 10))} className="w-28" />
                <span className="text-xs text-gray-600">{radiusMiles} mi</span>
              </div>
            )}
            {drawMode && (
              <>
                <span className="text-xs font-medium text-success">Draw: click points, double-click or Finish to close</span>
                <button onClick={finishDraw} className="px-2 py-1 text-xs rounded border bg-success-wash border-success/30 text-success">Finish</button>
                <button onClick={cancelDraw} className="px-2 py-1 text-xs rounded border bg-white border-gray-300 text-gray-600">Cancel</button>
              </>
            )}
            {tradeAreaMode && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-800">Trade area: tap any point</span>
                <div className="flex rounded-full border border-gray-300 overflow-hidden text-xs">
                  {([['distance', 'Distance'], ['drive', 'Drive time']] as const).map(([v, label]) => (
                    <button key={v} onClick={() => { setDriveTimeMode(v === 'drive'); setDriveContours(null); }}
                      className={`px-2.5 py-1 ${((v === 'drive') === driveTimeMode) ? 'bg-gray-900 text-white' : 'bg-white text-gray-800 hover:bg-gray-100'}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <span className="text-[11px] text-gray-500">{driveTimeMode ? '5 / 10 / 15 min drive' : '1 / 3 / 5 mi'}</span>
                {driveBusy && <span className="w-3 h-3 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />}
              </div>
            )}
            {/* Circle chips */}
            {circles.map((c) => (
              <span key={c.id} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-info-wash text-info border border-info/30">
                {c.miles} mi
                <button onClick={() => removeCircle(c.id)} className="hover:text-info">✕</button>
              </span>
            ))}
            {/* Polygon chip */}
            {polygon && (
              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-success-wash text-success border border-success/30">
                Drawn area ({polygonMatches?.length ?? 0})
                <button onClick={() => setPolygon(null)} className="hover:text-success">✕</button>
              </span>
            )}
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => setDealsOnly((v) => !v)}
                title="Show only properties priced below their submarket median $/SF"
                className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-full border whitespace-nowrap transition-colors ${dealsOnly ? 'bg-warning text-white border-warning' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
              >🔥<span className="hidden sm:inline"> Deal radar{dealsOnly ? ' · on' : ''}</span></button>
              <button
                onClick={() => setDistressOnly((v) => !v)}
                title="Show only properties with a loan maturing / past due (Capitalize distress data)"
                className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-full border whitespace-nowrap transition-colors ${distressOnly ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
              >🏦<span className="hidden sm:inline"> Distress{distressOnly ? ' · on' : ''}</span></button>
              {distressOnly && (
                <button
                  onClick={handlePushDistressToGhl}
                  disabled={ghlBusy}
                  title="Add the distressed owners in view to GoHighLevel for outreach (Do-Not-Call respected)"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-full border bg-info text-white border-info hover:bg-info/85 disabled:opacity-50 whitespace-nowrap"
                >{ghlBusy ? 'Pushing…' : '→ GHL'}</button>
              )}
              {/* Map/List toggle (mobile) */}
              <div className="lg:hidden flex rounded-full border border-gray-300 overflow-hidden">
                {(['map', 'list'] as const).map((v) => (
                  <button key={v} onClick={() => setMobileView(v)}
                    className={`px-3 py-1.5 text-xs font-medium ${mobileView === v ? 'bg-primary-500 text-white' : 'bg-white text-gray-600'}`}>
                    {v === 'map' ? 'Map' : 'List'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Content: map + list ── */}
        <div className="flex-1 min-h-0 flex">
          {/* Map */}
          <div className={`flex-1 min-h-0 relative ${mobileView === 'list' ? 'hidden' : ''} lg:block`}>
            {/* CoStar-style vertical tool rail (below the Leaflet zoom control):
                one unified card with hairline dividers; active state uses each
                tool's on-map color (blue radius / green draw / purple trade area). */}
            <div className="absolute z-[1000] left-2 top-[84px] flex flex-col rounded-xl overflow-hidden bg-white shadow-md ring-1 ring-gray-200 divide-y divide-gray-100">
              <button
                title="Radius search"
                onClick={toggleRadius}
                className={`w-9 h-9 flex items-center justify-center transition-colors ${radiusMode ? 'bg-info text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="8" strokeWidth={2} /><circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" /></svg>
              </button>
              <button
                title="Draw area (comps)"
                onClick={toggleDraw}
                className={`w-9 h-9 flex items-center justify-center transition-colors ${drawMode ? 'bg-success text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 20l4-1 9.5-9.5a2.1 2.1 0 00-3-3L5 16l-1 4z" /></svg>
              </button>
              <button
                title="Trade area rings (1/3/5 mi demographics)"
                onClick={toggleTradeArea}
                className={`w-9 h-9 flex items-center justify-center transition-colors ${tradeAreaMode ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="9" strokeWidth={1.5} /><circle cx="12" cy="12" r="5" strokeWidth={1.5} /><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" /></svg>
              </button>
            </div>
            <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} className="h-full w-full" style={{ height: '100%' }}>
              {/* FZ-FIX F10: Esri's street map - CARTO's answers every tile "API KEY REQUIRED" (lib/mapTiles.ts). */}
              <TileLayer {...STREET_TILES} />
              <MapController
                target={selectedProperty?.latitude && selectedProperty?.longitude
                  ? { id: selectedProperty.id, lat: selectedProperty.latitude, lng: selectedProperty.longitude }
                  : null}
                fit={searchFit}
                fitKey={aiComps ? `ai${aiNonce}` : searchText}
                drawing={drawMode}
                invalidateKey={mobileView}
              />
              <MapClickHandler radiusMode={radiusMode} drawMode={drawMode} tradeAreaMode={tradeAreaMode} onRadius={addCircle} onVertex={addVertex} onFinish={finishDraw} onTradeArea={handleTradeArea} />
              <BoundsWatcher onBounds={setBounds} />

              {/* Radius circles — draggable to move (center) / resize (edge) */}
              {circles.map((c) => (
                <RadiusCircleLayer key={c.id} circle={c} onResize={resizeCircle} onMove={moveCircle} onCommit={commitCircle} />
              ))}

              {/* Demographic distance rings (hidden while showing drive-time isochrones) */}
              {ringCenter && !driveContours && ringRadii.map((mi) => (
                <Circle key={mi} center={[ringCenter.lat, ringCenter.lng]} radius={mi * 1609.34}
                  pathOptions={{ color: '#7c3aed', weight: 1.5, fill: false, dashArray: '4 4' }} />
              ))}
              {/* Drive-time isochrones (largest first so inner contours layer darker) */}
              {ringCenter && driveContours && [...driveContours].sort((a, b) => b.minutes - a.minutes).map((c) => (
                <GeoJSON key={`${ringCenter.lat},${ringCenter.lng},${c.minutes}`} data={c.geometry}
                  style={{ color: '#7c3aed', weight: 1.5, fillColor: '#7c3aed', fillOpacity: 0.07 }} />
              ))}
              {/* Trade-area center marker (subject/target point) */}
              {ringCenter && (
                <Marker position={[ringCenter.lat, ringCenter.lng]} icon={ringCenterIcon} zIndexOffset={900}>
                  <Tooltip direction="top" offset={[0, -10]}>
                    <span className="font-medium">{ringCenter.isProperty ? ringCenter.label : 'Selected location'}</span>
                    {!ringCenter.isProperty && <><br /><span className="text-[10px] text-gray-500">{ringCenter.lat.toFixed(4)}, {ringCenter.lng.toFixed(4)}</span></>}
                  </Tooltip>
                </Marker>
              )}

              {/* In-progress + finished polygon */}
              {drawVertices.length > 0 && (
                <>
                  <Polyline positions={drawVertices} pathOptions={{ color: '#16a34a', dashArray: '4 4' }} />
                  {drawVertices.map((v, i) => (
                    <CircleMarker key={i} center={v} radius={4} pathOptions={{ color: '#16a34a', fillColor: '#fff', fillOpacity: 1 }} />
                  ))}
                </>
              )}
              {polygon && (
                <Polygon positions={polygon} pathOptions={{ color: '#16a34a', fillColor: '#16a34a', fillOpacity: 0.08 }} />
              )}

              {/* Properties (declustered at zoom 15 so a flown-to pin shows individually).
                  key flips browse<->search so the cluster cleanly rebuilds when the
                  search narrows the set. */}
              {layers.properties && (
                <MarkerClusterGroup key={mapItems === filtered ? 'browse' : 'search'} iconCreateFunction={clusterIcon} chunkedLoading maxClusterRadius={45} disableClusteringAtZoom={15} spiderfyOnMaxZoom={false}>
                  {mapItems.map((p) => (
                    <PropertyMarker
                      key={p.id}
                      p={p}
                      active={p.id === activeId}
                      selected={selectedIds.has(p.id)}
                      onSelect={handleSelect}
                      onHover={handleHover}
                    />
                  ))}
                </MarkerClusterGroup>
              )}

              {/* Selected property: dedicated always-visible marker + open popup */}
              {layers.properties && selectedProperty && selectedProperty.latitude && selectedProperty.longitude && (
                <SelectedPropertyMarker
                  key={selectedProperty.id}
                  p={selectedProperty}
                  selected={selectedIds.has(selectedProperty.id)}
                  onHover={handleHover}
                  onDemographics={handleDemographics}
                  onToggleSelect={toggleSelect}
                />
              )}

              {/* Permits */}
              {layers.permits && (
                <MarkerClusterGroup iconCreateFunction={clusterIcon} chunkedLoading>
                  {permitPins.map((p) => (
                    <Marker key={p.id} position={[p.latitude!, p.longitude!]} icon={permitDiamondIcon(!!p.is_industrial)}>
                      <Popup maxWidth={280} minWidth={200}>
                        <div className="text-xs space-y-1.5">
                          <p className="font-semibold text-sm text-gray-900">{p.permit_type || 'Permit'}{p.is_industrial ? ' • Industrial' : ''}</p>
                          <p className="text-gray-500">{[p.property_address, p.jurisdiction].filter(Boolean).join(' • ')}</p>
                          {p.description && <p className="text-gray-700">{p.description}</p>}
                          <div className="grid grid-cols-2 gap-x-3 gap-y-1 pt-1">
                            {p.status && <div><span className="text-gray-400">Status:</span> {p.status}</div>}
                            {p.valuation ? <div><span className="text-gray-400">Value:</span> {formatCurrency(p.valuation)}</div> : null}
                            {p.issue_date && <div><span className="text-gray-400">Issued:</span> {p.issue_date}</div>}
                            {p.permit_number && <div><span className="text-gray-400">No.:</span> {p.permit_number}</div>}
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              )}

              {/* Tenants (CoStar lease-expiration prospects); pin color = urgency */}
              {layers.tenants && (
                <MarkerClusterGroup iconCreateFunction={clusterIcon} chunkedLoading>
                  {tenantPins.map((t) => (
                    <Marker key={t.id} position={[t.latitude!, t.longitude!]} icon={tenantIcon(t)}>
                      <Popup maxWidth={280} minWidth={210}>
                        <div className="text-xs space-y-1.5">
                          <p className="font-semibold text-sm text-gray-900">{t.tenant_name}</p>
                          <p className="text-gray-500">{[t.address, t.city].filter(Boolean).join(' • ')}</p>
                          {t.lease_expiration && (
                            <p className="font-semibold" style={{ color: tenantExpiryColor(t.lease_expiration) }}>
                              Lease expires {new Date(t.lease_expiration + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                            </p>
                          )}
                          <div className="grid grid-cols-2 gap-x-3 gap-y-1 pt-1">
                            {t.contact_name && <div><span className="text-gray-400">Contact:</span> {t.contact_name}</div>}
                            {t.phone && <div><span className="text-gray-400">Phone:</span> {t.phone}</div>}
                            {t.sf_occupied ? <div><span className="text-gray-400">SF:</span> {t.sf_occupied.toLocaleString()}</div> : null}
                            {t.industry && <div className="col-span-2"><span className="text-gray-400">Industry:</span> {t.industry}</div>}
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCallInGhl(t); }}
                            className="mt-1 w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-primary-500 text-white text-xs font-semibold px-2.5 py-1.5 hover:bg-primary-600 transition-colors"
                          >
                            📞 Call in GHL
                          </button>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              )}
            </MapContainer>
          </div>

          {/* Results list */}
          <div className={`w-full lg:w-[440px] shrink-0 border-l bg-white flex flex-col ${mobileView === 'map' ? 'hidden' : ''} lg:flex`}>
            {/* Count + actions header (search lives in the top filter bar) */}
            <div className="shrink-0 p-2.5 border-b bg-white">
              <div className="flex items-center justify-between px-0.5">
                <p className="text-xs text-gray-500">
                  {hasSelection ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="font-semibold text-gray-800">{selected.size} selected for comps</span>
                      <button onClick={clearSelected} className="text-gray-800 hover:text-gray-800 underline">Clear</button>
                    </span>
                  ) : (
                    <>
                      {listItems.length.toLocaleString()} propert{listItems.length === 1 ? 'y' : 'ies'}
                      {searchText ? ' (all)' : selection ? ' in area' : ' in view'}
                    </>
                  )}
                </p>
                {(listItems.length > 0 || hasSelection) && (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setCompareOpen(true)}
                      className="px-2 py-1 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                    >Compare{hasSelection ? ` (${selected.size})` : ''}</button>
                    <button
                      onClick={() => exportCompsCsv(actionItems, hasSelection ? 'apex-comps-selected.csv' : selection ? 'apex-comps-area.csv' : 'apex-comps.csv')}
                      className="px-2 py-1 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                    >Export CSV</button>
                    <button
                      onClick={() => handleCompReport(actionItems, hasSelection ? 'Selected comps' : selection ? 'Drawn area' : searchText ? `Search: ${searchText}` : 'Map view')}
                      disabled={reportBusy}
                      className="px-2.5 py-1 text-xs font-semibold rounded-md bg-primary-500 text-white border border-primary-500 shadow-sm hover:bg-primary-600 hover:border-primary-600 transition-colors disabled:opacity-50"
                    >{reportBusy ? 'Building…' : 'Comp Report PDF'}</button>
                    <button
                      onClick={() => setSaveCompsOpen(true)}
                      className="px-2 py-1 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                    >Save as comps</button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading
                ? <div className="p-6 text-center text-gray-400 text-sm">Loading…</div>
                : <MapResultsList
                    items={listItems}
                    hoveredId={hoveredId}
                    selectedId={selectedId}
                    onHover={handleHover}
                    onSelect={handleSelect}
                    selectedIds={selectedIds}
                    onToggleSelect={toggleSelect}
                    onSelectAllVisible={selectAllVisible}
                    onClearVisible={clearVisible}
                    dealFlags={dealFlags}
                  />}
            </div>
          </div>
        </div>

        {/* Demographics drawer */}
        {ringCenter && (
          <div className="fixed inset-y-0 right-0 z-[1500] w-full max-w-md bg-white shadow-2xl border-l overflow-y-auto">
            <div className="flex items-center justify-between p-3 border-b sticky top-0 bg-white z-10">
              <div>
                <p className="text-sm font-semibold text-gray-900 truncate">{ringCenter.label}</p>
                <p className="text-[11px] text-gray-400">{driveContours !== null ? '5 / 10 / 15-minute drive time' : '1 / 3 / 5-mile trade area'}</p>
              </div>
              <button onClick={() => { setRingCenter(null); setDriveContours(null); }} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100" aria-label="Close demographics">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-3">
              {driveContours !== null ? (
                driveBusy ? (
                  <p className="text-sm text-gray-400 text-center py-6">Calculating drive-time trade area…</p>
                ) : driveContours.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center">No drive-time data available for this point.</p>
                ) : (
                  <div className="space-y-3">
                    {driveContours.map((c: any) => (
                      <div key={c.minutes} className="border border-gray-200 rounded-lg p-3">
                        <p className="text-sm font-semibold text-gray-800 mb-2">{c.minutes}-minute drive</p>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                          <div><span className="text-gray-400">Population</span><br /><span className="font-semibold text-gray-900">{Number(c.population).toLocaleString()}</span></div>
                          <div><span className="text-gray-400">Households</span><br /><span className="font-semibold text-gray-900">{Number(c.households).toLocaleString()}</span></div>
                          <div><span className="text-gray-400">Median income</span><br /><span className="font-semibold text-gray-900">{c.median_household_income ? '$' + Number(c.median_household_income).toLocaleString() : '—'}</span></div>
                          <div><span className="text-gray-400">Median age</span><br /><span className="font-semibold text-gray-900">{c.median_age ?? '—'}</span></div>
                          <div><span className="text-gray-400">Owner / Renter</span><br /><span className="font-semibold text-gray-900">{Number(c.owner_occupied).toLocaleString()} / {Number(c.renter_occupied).toLocaleString()}</span></div>
                          <div><span className="text-gray-400">Bachelor's+</span><br /><span className="font-semibold text-gray-900">{Number(c.bachelors_plus).toLocaleString()}</span></div>
                        </div>
                      </div>
                    ))}
                    <p className="text-[11px] text-gray-400">U.S. Census ACS + Mapbox drive-time. Area-weighted estimates.</p>
                    {/* Premium (paid, on-demand) spending + Tapestry for the same drive-time polygons. */}
                    <SpendingSegmentsPanel polygons={driveContours.map((c: any) => ({ minutes: c.minutes, geometry: c.geometry }))} />
                  </div>
                )
              ) : (
                <DemographicsPanel
                  lat={ringCenter.lat}
                  lng={ringCenter.lng}
                  radii={ringRadii}
                  onRadiiChange={setRingRadii}
                  subject={{ label: ringCenter.label, lat: ringCenter.lat, lng: ringCenter.lng, isProperty: !!ringCenter.isProperty }}
                />
              )}
            </div>
          </div>
        )}

        {/* Compare selected properties (reuses the shipped comparison modal) */}
        {compareOpen && (
          <PropertyComparison
            properties={actionItems.slice(0, 50).map(toComparisonProperty)}
            onClose={() => setCompareOpen(false)}
          />
        )}

        {/* Save the current selection as comps on a subject property */}
        {saveCompsOpen && (
          <SaveCompsModal
            compIds={actionItems.slice(0, 50).map((p) => p.id)}
            pool={properties}
            onClose={() => setSaveCompsOpen(false)}
          />
        )}

        {/* All Filters drawer */}
        {filtersOpen && (
          <MapFiltersPanel
            filter={filter}
            onChange={setFilter}
            onClose={() => setFiltersOpen(false)}
            onReset={() => setFilter({ ...EMPTY_FILTERS })}
            resultCount={listItems.length}
            options={filterOptions}
          />
        )}
      </div>
    </Layout>
  );
}
